import { useState } from "react";
import { Download, BookOpen, CheckCircle2, Copy, Mail, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
function ConfirmationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const isOver18 = useWatch({
    control,
    name: "isOver18",
    defaultValue: true
  });
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center text-center p-8 bg-white/5 rounded-2xl border border-white/10 animate-in fade-in zoom-in">
        <CheckCircle2 className="size-12 text-secondary mb-4" />
        <h4 className="text-xl font-bold text-white mb-2">{successMessage}</h4>
        <p className="text-primary-foreground/70 mb-6 text-sm">Our team will verify your transaction details shortly.</p>
        <Button onClick={resetForm} variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full px-8">
          Submit Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="FirstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
              </FormControl>
              <FormMessage className="text-destructive text-xs" />
            </FormItem>} />

        <FormField control={control} name="LastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
              </FormControl>
              <FormMessage className="text-destructive text-xs" />
            </FormItem>} />
        
        <FormField control={control} name="amountUsdt" rules={{
        required: "Amount is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">Amount (USDT)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="1000" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
              </FormControl>
              <FormMessage className="text-destructive text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="transactionHash" rules={{
      required: "Transaction hash is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">Transaction Hash</FormLabel>
            <FormControl>
              <Input placeholder="Paste transaction hash" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

            <FormField control={control} name="EmailAddress" rules={{
      required: "Email Address is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">Email Address</FormLabel>
            <FormControl>
              <Input placeholder="Paste Email Address" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      <FormField control={control} name="walletAddress" rules={{
      required: "Receiving wallet address is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">Receiving Wallet Address</FormLabel>
            <FormControl>
              <Input placeholder="0x..." {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
            </FormControl>
            <FormDescription className="text-primary-foreground/50 text-[10px] leading-tight">
              If left empty, we will use the wallet address that sent the USDT by default
            </FormDescription>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      <FormField control={control} name="referralCode" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">Referral Code (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="REF-123" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
            </FormControl>
            <FormMessage className="text-destructive text-xs" />
          </FormItem>} />

      <FormField control={control} name="isOver18" render={({
      field
    }) => <FormItem>
      <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
        <FormControl>
          <Checkbox checked={field.value ?? true} onCheckedChange={field.onChange} className="mt-0.5 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black" />
        </FormControl>
        <div className="space-y-1">
          <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">
            I confirm that I am at least 18 years old
          </FormLabel>
        </div>
      </div>
      <FormMessage className="text-destructive text-xs" />
    </FormItem>} />

    {!isOver18 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <FormField control={control} name="responsibleFirstName" rules={{
        required: "Responsible first name is required"
      }} render={({
        field
      }) => <FormItem>
          <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">
            Parent / Legal Guardian First Name
          </FormLabel>
          <FormControl>
            <Input placeholder="John" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
          </FormControl>
          <FormMessage className="text-destructive text-xs" />
        </FormItem>} />

    <FormField control={control} name="responsibleLastName" rules={{
        required: "Responsible last name is required"
      }} render={({
        field
      }) => <FormItem>
          <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">
            Parent / Legal Guardian Last Name
          </FormLabel>
          <FormControl>
            <Input placeholder="Doe" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
          </FormControl>
          <FormMessage className="text-destructive text-xs" />
        </FormItem>} />

    <FormField control={control} name="responsibleEmailAddress" rules={{
        required: "Responsible email address is required"
      }} render={({
        field
      }) => <FormItem className="sm:col-span-2">
          <FormLabel className="text-primary-foreground/80 text-xs font-bold uppercase tracking-wider">
            Parent / Legal Guardian Email Address
          </FormLabel>
          <FormControl>
            <Input type="email" placeholder="guardian@email.com" {...field} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-secondary focus:ring-secondary" />
          </FormControl>
          <FormMessage className="text-destructive text-xs" />
        </FormItem>} />
  </div>}
      {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-lg text-sm text-white">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 text-base font-bold rounded-full">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : "Submit Confirmation"}
      </Button>
    </Form>;
}
export default function AccessAndInstallation() {
  const [activeStep, setActiveStep] = useState(0);
  const [copied, setCopied] = useState(false);
  const steps = [{
    number: "01",
    title: "Send USDT",
    subtitle: "Send USDT",
    description: "Send USDT only on the Ethereum ERC20 network to the official Aizalea wallet. Make sure you use a compatible wallet and save your transaction hash.",
    cardLabel: "Wallet Address (ERC20)",
    cardValue: "0xBC5A50390B071e31E3440781e3C24203938DB850",
    check1: "USDT only",
    check2: "Ethereum ERC20 only",
    check3: "Use a compatible wallet",
    check4: "Save your transaction hash"
  }, {
    number: "02",
    title: "Submit Confirmation",
    subtitle: "Submit Confirmation",
    description: "After your transfer, send your transaction details so we can verify and allocate your tokens.",
    cardLabel: "Email",
    cardValue: "token@aizahcare.com",
    check1: "Transaction hash",
    check2: "Full name",
    check3: "Receiving wallet address",
    check4: "Referral code (optional)"
  }, {
    number: "03",
    title: "Verification",
    subtitle: "Verification",
    description: "Our team verifies your transaction on the blockchain and confirms your token allocation, usually within less than 48 hours..",
    cardLabel: "",
    cardValue: "",
    check1: "Transaction verified on the blockchain",
    check2: "Secure allocation process",
    check3: "Confirmation email sent",
    check4: ""
  }, {
    number: "04",
    title: "Receive Tokens",
    subtitle: "Receive Tokens",
    description: "Once your transaction has been verified, your Aizalea tokens are allocated to your wallet. Pre-sale tokens are subject to a 3-month lock-up period, followed by a vesting schedule with 25% released every 3 months.",
    cardLabel: "Delivery",
    cardValue: "Instant Allocation",
    requirementsTitle: "What Happens Next",
    check1: "Tokens allocated to your wallet",
    check2: "3-month lock-up for pre-sale tokens",
    check3: "25% released every 3 months",
    check4: "Confirmation receipt sent by email"
  }];
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <section data-section-id="150580" id="howtobuyaizalea" className="bg-background py-24 relative overflow-hidden" dir="ltr">
      
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-[30rem] h-[30rem] bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 gap-16 items-center">

          
          <div className="flex flex-col items-center text-center">
            <h2 className="text-4xl md:text-5xl font-extrabold font-sans text-foreground mb-10 tracking-tight leading-tight max-w-3xl">
              How to Buy Aizalea Tokens
            </h2>

            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 w-full max-w-4xl justify-center">
              {steps.map((step, i) => <Button key={i} data-index={i} variant={activeStep === i ? "default" : "outline"} onClick={() => setActiveStep(i)} className={`h-auto flex-col items-center p-4 gap-1 text-center transition-all duration-300 ${activeStep === i ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "hover:border-primary/50"}`}>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest opacity-70">
                    {step.number} - {step.subtitle}
                  </span>
                  <span className="font-bold text-sm sm:text-base">
                    {step.title}
                  </span>
                </Button>)}
            </div>

            
            <div className="relative w-full max-w-4xl mx-auto">
              <Card className="bg-primary text-primary-foreground border-none shadow-2xl overflow-hidden mb-12">
                <CardContent className="pt-12 pb-12 flex flex-col items-center">
                  <div className="flex flex-col items-center gap-4 mb-6">
                    <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground font-mono text-lg py-1 px-3">
                      {steps[activeStep].number}
                    </Badge>
                    <div className="flex items-center gap-3">
                      <h3 className="text-3xl font-bold tracking-tight">
                        {steps[activeStep].title}
                      </h3>
                      {activeStep === 3 && <Badge variant="secondary" className="bg-secondary text-secondary-foreground border-none text-[10px] uppercase tracking-wider px-2 py-0.5">
                          3-month lock-up
                        </Badge>}
                    </div>
                  </div>
                  <p className="text-primary-foreground/90 text-lg leading-relaxed mb-10 max-w-2xl">Our team verifies your transaction on the blockchain and confirms your token allocation, usually within 48 hours.</p>

                  <div className="w-full flex flex-col items-center gap-10 pt-10 border-t border-primary-foreground/10">
                    
                    {activeStep === 0 && <div className="flex flex-col items-center gap-4">
                        <div className="text-lg font-bold text-primary-foreground break-all font-mono text-center">
                          Wallet Address (ERC20) 0xBC5A50390B071e31E3440781e3C24203938DB850
                        </div>
                        <Tooltip text="Copy to clipboard">
                          <Button variant="outline" size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border bg-background shadow-xs h-10 has-[>svg]:px-4 border-primary-foreground/30 rounded-full px-8 !text-[hsl(0,0%,0%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-primary-foreground/10" onClick={() => handleCopy("0xBC5A50390B071e31E3440781e3C24203938DB850")}>Copy wallet address</Button>
                        </Tooltip>
                        {copied && <span className="text-xs font-bold text-white animate-in fade-in slide-in-from-top-1">Address copied!</span>}

                        <div className="flex flex-col items-center gap-3">
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground/60">Scan QR Code</span>
                          <div className="bg-white p-4 rounded-2xl shadow-lg border border-white/10 flex items-center justify-center">
                            <img src="https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/PHOTO-2026-03-26-11-54-46-3.jpeg" alt="Wallet QR Code" className="size-32 object-contain" />
                          </div>
                        </div>
                      </div>}

                    {activeStep === 1 && <div className="w-full max-w-xl mx-auto">
                        <FormProvider formId="private-round-confirmation" sectionName="access-and-installation" defaultValues={{
                      fullName: "",
                      transactionHash: "",
                      amountUsdt: "",
                      EmailAddress: "",
                      walletAddress: "",
                      referralCode: "",
                      isOver18: true,
                      responsibleFirstName: "",
                      responsibleLastName: "",
                      responsibleEmailAddress: ""
                    }} submitText="Submit Confirmation" successMessage="Your verification request has been sent">
                          <ConfirmationForm />
                        </FormProvider>
                      </div>}

                    <div className="space-y-4 w-full">
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground/50">Verification Requirements</h4>
                      <ul className="flex flex-wrap justify-center gap-x-8 gap-y-3 max-w-3xl mx-auto">
                        {[steps[activeStep].check1, steps[activeStep].check2, steps[activeStep].check3, steps[activeStep].check4].filter(Boolean).map((item, i) => <li key={i} data-index={i} className="flex items-center gap-3 text-primary-foreground/90">
                            <CheckCircle2 className="size-4 text-secondary shrink-0" />
                            <span className="text-sm font-medium leading-none">{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="absolute -top-6 -right-6 bg-background border border-border shadow-lg rounded-2xl p-4 flex items-center gap-3 animate-bounce z-20" style={{
              animationDuration: '3s'
            }}>
                <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-medium text-foreground whitespace-nowrap">Network: Ethereum ERC20</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4">
              
              <Button variant="ghost" size="lg" asChild className="rounded-full h-14 px-6 text-muted-foreground hover:text-foreground hover:bg-accent/10">
                
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}